import { useEffect, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Loader2, Save, Lock, Eye, CheckCircle2, AlertCircle } from "lucide-react";
import { api, errMsg } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";

export const LetterManager = () => {
  const qc = useQueryClient();
  const { data } = useQuery({ queryKey: ["admin-letter-template"], queryFn: () => api.get("/admin/letter/template").then((r) => r.data) });
  const { data: log } = useQuery({ queryKey: ["admin-letter-log"], queryFn: () => api.get("/admin/letter/log").then((r) => r.data) });
  const [sections, setSections] = useState({});
  const [subject, setSubject] = useState("");
  const [busy, setBusy] = useState(false);
  const [preview, setPreview] = useState(null);

  useEffect(() => {
    if (data) { setSections(data.sections); setSubject(data.subject); }
  }, [data]);

  if (!data) return null;
  const missing = data.section_meta.filter((m) => !(sections[m.key] || "").trim()).length;

  const save = async () => {
    setBusy(true);
    try {
      await api.put("/admin/letter/template", { sections, subject });
      toast.success(missing ? `Saved — ${missing} section${missing === 1 ? "" : "s"} still empty, letter stays hidden` : "Saved — the letter is live for F.E.R.Ms");
      qc.invalidateQueries({ queryKey: ["admin-letter-template"] });
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };

  const replay = async () => {
    setBusy(true);
    try {
      const { data: r } = await api.post("/admin/points/replay");
      toast.success(`Pushed to the WOW ledger — ${r.sent} sent, ${r.duplicate} already there, ${r.failed} failed`);
      qc.invalidateQueries({ queryKey: ["admin-letter-log"] });
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };

  const doPreview = async () => {
    try {
      const { data: p } = await api.post("/admin/letter/preview", { recipient_is_sitting_member: true, local_matter: { matter: "Three applications in this division have been refused on cultural heritage grounds since March, with no published reasons.", instrument: "Aboriginal and Torres Strait Islander Heritage Protection Act 1984, section 10", ask: "Will you ask the Minister to publish the reasons for section 10 decisions as a matter of course?" } });
      setPreview(p.text);
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  const cell = "bg-[#121E2B] border-[#2C3E55] text-sm";
  return (
    <div className="space-y-6" data-testid="admin-letter-manager">
      <div className="rounded-2xl bg-[#1B2A41] border border-[#2C3E55] p-5 flex flex-col sm:flex-row sm:items-center gap-4 justify-between">
        <div>
          <h3 className="font-type font-bold text-lg">F.E.R.M letter template</h3>
          <p className="text-xs text-[#F8FAFC]/50 mt-1">{data.source === "default" ? "Showing the agreed wording (June 2026 letter). Edit and save to override it. " : "Custom wording saved by an admin. "}Locked sections can never be edited by members. Merge fields: {data.merge_fields.map((f) => <code key={f} className="font-type text-[#8FA58A] mr-1.5">{`{{${f}}}`}</code>)}</p>
        </div>
        <div className="flex items-center gap-3 flex-shrink-0">
          <span data-testid="admin-letter-status" className={`inline-flex items-center gap-1.5 text-xs px-3 h-8 rounded-full border ${data.ready ? "border-[#8FA58A] text-[#8FA58A]" : "border-[#E2705A]/60 text-[#E2705A]"}`}>
            {data.ready ? <CheckCircle2 className="w-3.5 h-3.5" /> : <AlertCircle className="w-3.5 h-3.5" />} {data.ready ? "Live for F.E.R.Ms" : `Hidden — ${missing} section${missing === 1 ? "" : "s"} empty`}
          </span>
          <span className="text-xs text-[#F8FAFC]/50" data-testid="admin-letter-log-count">{log?.total ?? 0} generated · {log?.with_local_matter ?? 0} with a local matter{log?.first_letter_points && <> · {log.first_letter_points.points_each} WOW pts per first letter ({log.first_letter_points.rule_id}): {log.first_letter_points.sent} sent, {log.first_letter_points.queued} queued, {log.first_letter_points.failed} failed{log.first_letter_points.ledger_configured ? "" : " — ROAN_POINTS_KEY not set"}</>}</span>
          {log?.first_letter_points && (log.first_letter_points.queued > 0 || log.first_letter_points.failed > 0) && (
            <button type="button" onClick={replay} disabled={busy} data-testid="admin-points-replay" className="h-8 px-3 rounded-full border border-[#8FA58A]/60 text-[#8FA58A] hover:bg-[#8FA58A]/10 text-xs font-type font-bold uppercase tracking-wider disabled:opacity-50">Push queued points</button>
          )}
        </div>
      </div>

      <div className="rounded-2xl bg-[#1B2A41] border border-[#2C3E55] p-5 space-y-5">
        <div className="space-y-1.5">
          <Label className="font-type text-[11px] uppercase tracking-wider text-[#8FA58A]">Email subject line</Label>
          <Input data-testid="admin-letter-subject" value={subject} onChange={(e) => setSubject(e.target.value)} className={cell} />
        </div>
        {data.section_meta.map((m) => (
          <div key={m.key} className="space-y-1.5">
            <Label className="font-type text-[11px] uppercase tracking-wider text-[#8FA58A] inline-flex items-center gap-1.5">{m.locked && <Lock className="w-3 h-3" />} {m.label}</Label>
            <Textarea data-testid={`admin-letter-section-${m.key}`} value={sections[m.key] || ""} onChange={(e) => setSections({ ...sections, [m.key]: e.target.value })} rows={m.key === "signoff" ? 4 : 5} className={`${cell} leading-relaxed`} placeholder="Paste the wording for this section…" />
          </div>
        ))}
        <div className="flex flex-wrap gap-2">
          <button type="button" onClick={save} disabled={busy} data-testid="admin-letter-save" className="inline-flex items-center gap-2 h-10 px-5 rounded-full bg-[#E2705A] hover:bg-[#D35D46] disabled:opacity-60 text-xs font-type font-bold uppercase tracking-wider">{busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} Save template</button>
          <button type="button" onClick={doPreview} disabled={!data.ready} data-testid="admin-letter-preview" className="inline-flex items-center gap-2 h-10 px-5 rounded-full border border-[#2C3E55] hover:border-[#8FA58A] disabled:opacity-40 text-xs font-type font-bold uppercase tracking-wider"><Eye className="w-4 h-4" /> Preview (sample F.E.R.M, Brisbane, with local matter)</button>
        </div>
        {preview && <Textarea readOnly value={preview} rows={18} data-testid="admin-letter-preview-text" className="bg-[#121E2B] border-[#2C3E55] text-xs font-mono leading-relaxed" />}
      </div>
    </div>
  );
};
